import React, { useCallback } from 'react';

// Lib
import { useEvent } from 'lib/hooks';
import { useAppDispatch } from 'lib/hooks/useDispatch';

// Actions
import { showLoader } from 'reducers/loader';
import { showMessage } from 'reducers/messages';
import * as actionsCreator from 'pages/Search/reducer';

// Constants
import { isEmpty, messageObj } from 'constans/marvin';
import { getFormData, structureFormKeys } from 'pages/Search/utils';

// Type
type Args = {
	errors?: any;
	sketcher: any;
	goTo?: number;
	getValues: any;
	totalPages?: number;
	typeData: string | null;
	drv: any
};

export const useStructureHandler = (props: Args) => {
	const {
		drv,
		goTo,
		errors,
		sketcher,
		typeData,
		totalPages,
		getValues
	} = props;
	const dispatch = useAppDispatch();

	const handleClickType = useEvent((type: string) => {
		dispatch(
			actionsCreator.handlerChangeTypeStructure(
				type === 'EBC' ? 'compounds' : type
			)
		);
	});

	const onSubmit = useEvent((data: any) => {
		if (!!Object?.values(errors)?.length) {
			return;
		}

		const linkers = getValues('linkers');
		const compounds = getValues('compounds');

		if (!Object?.values(sketcher || {})?.length) return;

		sketcher?.exportStructure('mrv').then((source: string) => {
			if (source === isEmpty) {
				dispatch(showMessage(messageObj));
				return;
			}

			if (!linkers && !compounds) {
				dispatch(
					showMessage({
						type: 'info',
						text: 'Choose the type of criteria'
					})
				);
				return;
			}

			dispatch(
				actionsCreator.getStructureSearch({
					isClearState: true,
					data: { form: data, source }
				})
			);
		});

		if(drv?.isActive()) {
			setTimeout(() => {
				drv?.moveNext();
			}, 500);
		}
	});

	const getRequestPagination = useCallback(
		(number: number) => {
			if (!typeData) return;

			if (typeData === 'compounds')
				dispatch(actionsCreator.handleChangeStructurePageEbc(number));
			if (typeData === 'linkers')
				dispatch(actionsCreator.handleChangeStructurePageLinkers(number));

			sketcher?.exportStructure('mrv').then((source: string) => {
				if (source === isEmpty) return dispatch(showMessage(messageObj));

				const formData = getFormData(structureFormKeys, getValues);

				if (!formData.linkers && !formData.compounds) {
					return dispatch(
						showMessage({ type: 'info', text: 'Choose the type of search' })
					);
				}

				dispatch(actionsCreator.getStructureSearch({ data: { form: formData, source } }));
				dispatch(showLoader(true));
			});
		},
		[sketcher, typeData, getValues, dispatch]
	);

	const onPagePress = useEvent((number: number) => {
		getRequestPagination(number - 1);
	});

	const onNextPressLast = useEvent(() => {
		getRequestPagination((totalPages || 0) - 1);
	});

	const onPrevPressLast = useEvent(() => {
		getRequestPagination(0);
	});

	const onKeyPress = useEvent((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			const inputElement = e.target as HTMLInputElement;
			getRequestPagination(+inputElement.value - 1);
		}
	});

	const handlerSubmitGoTo = useEvent(() => {
		if (!goTo) return;
		getRequestPagination(goTo - 1);
	});

	const handlerChangeGoTo = useEvent((event: React.ChangeEvent<HTMLInputElement>) => {
		const number = +event?.target?.value > 0 ? +event?.target?.value : 1;

		if (number <= (totalPages || 0)) {
			dispatch(
				actionsCreator.handlerChangeStructureGoTo({
					goTo: number,
					type: typeData
				})
			);
		} else {
			dispatch(
				showMessage({
					type: 'info',
					text: 'This is the last record'
				})
			);
		}
	});

	const handlerChangeSize = useEvent((data: { value: number }) => {
		if (!typeData) return;

		dispatch(
			actionsCreator.handlerChangeStructureSize({
				type: typeData,
				size: data.value
			})
		);

		getRequestPagination(0);
	});

	return {
		onSubmit,
		onKeyPress,
		onPagePress,
		handleClickType,
		onNextPressLast,
		onPrevPressLast,
		handlerSubmitGoTo,
		handlerChangeGoTo,
		handlerChangeSize
	};
};
